/**
 * KnowledgeDetailPage — J7-W4 task 6a (PRD §12.3.10)
 *
 * Full editor for a single Knowledge Item. Sections:
 *   1. Header — title, item_type badge, SoT badge, FreshnessBadge.
 *   2. Body — title + description + body content (markdown supported via
 *      Textarea — no rich text editor in the boilerplate UI library yet).
 *   3. Metadata — SoT toggle, item type, source reference URL, curator, domain,
 *      relevant domains.
 *   4. Evidence Attachments — AttachmentsManager picker (mirrors F3.5 pattern).
 *   5. Linked Work Items panel — read-only list filtered by the explicit
 *      knowledge_item_id FK on work items (ART-03; closes CLEAN-J7-W5-Q1).
 *   6. Freshness review — banner when overdue + "Mark as reviewed" action.
 *
 * Routes:
 *   /roros/artifacts/knowledge/:id        — detail (read view + Save action)
 *   /roros/artifacts/knowledge/:id/edit   — same page in edit mode (alias)
 *   /roros/artifacts/knowledge/new        — create mode (delegates to dialog
 *                                            on KnowledgeItemsPage today; this
 *                                            page is for existing items only)
 */

import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, CheckCircle2, Star } from 'lucide-react';
import { t } from '@/lib/i18n';
import { RorosPage } from '@/components/roros/RorosPage';
import { Card, CardContent } from '@/components/ui/Card/Card';
import { Badge } from '@/components/ui/Badge/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Checkbox } from '@/components/ui/Checkbox';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { LoadingState } from '@/components/ui/LoadingState';
import { EmptyState } from '@/components/ui/EmptyState';
import { Alert } from '@/components/ui/Alert';
import { showToast } from '@/components/ui/Toast';
import { AttachmentsManager } from '@/components/Attachments/AttachmentsManager';
import { FreshnessBadge, computeFreshnessState } from '@/components/roros/FreshnessBadge';
import { ExtractionStatusPanel } from '@/components/roros/ExtractionStatusPanel';
import {
  useRorosKnowledgeItem,
  useRorosKnowledgeUpdate,
  useRorosKnowledgeMarkReviewed,
  useRorosKnowledgeReprocess,
} from '@/hooks/roros/artifacts';
import { useRorosDomains } from '@/hooks/roros/domains';
import { useRorosWorkItems } from '@/hooks/roros/control-tower';
import { useRorosActors } from '@/hooks/roros/actors';
import { formatDate } from '@/lib/localeFormatters';
import type { RorosKnowledgeItemDTO, UpdateKnowledgeItemPayload } from '@/types/roros/artifacts';
import type { RorosDomainDTO } from '@/types/roros/domains';
import type { RorosActorDTO } from '@/types/roros/actors';
import type { RorosWorkItemDTO } from '@/types/roros/control-tower';
import { usePermissions } from '@/hooks/usePermissions';
import { PERMISSIONS } from '@/constants/permissions';
import { useAuthStore } from '@/stores/authStore';

const ITEM_TYPE_OPTIONS = [
  { value: 'document', label: t('Document') },
  { value: 'fact', label: t('Fact') },
  { value: 'template', label: t('Template') },
  { value: 'checklist', label: t('Checklist') },
  { value: 'standard', label: t('Standard') },
  { value: 'decision_log', label: t('Decision Log') },
  { value: 'method_library', label: t('Method Library') },
  { value: 'advisory_board', label: t('Advisory Board') },
  { value: 'source_of_truth', label: t('Source of Truth') },
];

export function KnowledgeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const currentUserId = useAuthStore((s) => s.user?.id ?? null);

  // The detail GET uses /api/v1/domains/system/knowledge/:id by default
  // (no domain in path matches the system route on the server). Once we have
  // the item we know its real domainId for subsequent PUT calls.
  const itemQuery = useRorosKnowledgeItem('system', id ?? '');
  const item = itemQuery.data as RorosKnowledgeItemDTO | undefined;

  // Domain for the PUT call. System items POST to /domains/system/knowledge.
  const writeDomainPath = item?.domainId ?? 'system';

  const updateMutation = useRorosKnowledgeUpdate();
  const reviewMutation = useRorosKnowledgeMarkReviewed();
  const reprocessMutation = useRorosKnowledgeReprocess();

  const domainsQuery = useRorosDomains();
  const domains = (domainsQuery.data ?? []) as RorosDomainDTO[];

  const actorsQuery = useRorosActors();
  const actors = (actorsQuery.data ?? []) as RorosActorDTO[];

  // ART-03 (CLEAN-J7-W5-Q1 closure): list work items with the explicit
  // knowledge_item_id FK introduced by Migration 344 (J7-W5). Previously
  // this filtered by domain as a soft proxy — that surfaced unrelated items
  // and missed cross-domain links. The backend filter is wired in
  // listWorkItemsQuerySchema (knowledgeItemId).
  const workItemsQuery = useRorosWorkItems(item?.id ? { knowledgeItemId: item.id } : undefined);
  const linkedWorkItems = (workItemsQuery.data ?? []) as RorosWorkItemDTO[];

  // ─── Local form state ─────────────────────────────────────────────────
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [body, setBody] = useState('');
  const [itemType, setItemType] = useState('document');
  // AC-K-5: sourceReference state retained for save payload compatibility
  // but the field is no longer shown in the form. Column kept in DB.
  const [sourceReference, setSourceReference] = useState('');
  const [isSourceOfTruth, setIsSourceOfTruth] = useState(false);
  const [curatedByActorId, setCuratedByActorId] = useState<string>('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [evidenceAttachmentIds, setEvidenceAttachmentIds] = useState<string[]>([]);
  // J7-W4 fix-wave 2 (B5) — dirty tracking. The hydration useEffect previously
  // re-ran on EVERY React Query refetch (e.g. after Mark-Reviewed invalidates
  // the item key) and silently overwrote in-progress edits. We now hydrate
  // exactly once per item id: the first render with `item` defined sets the
  // form, marks it pristine, and any setTitle/setBody/etc. flips dirty so
  // subsequent refetches do NOT reset the form.
  const [isDirty, setIsDirty] = useState(false);
  const hydratedForItemIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!item) return;
    // Re-hydrate if the route id changed (different item) OR if we have
    // never hydrated this id and the form is still pristine.
    const idChanged = hydratedForItemIdRef.current !== item.id;
    if (!idChanged && isDirty) return; // user has unsaved edits — leave them alone
    if (!idChanged && hydratedForItemIdRef.current === item.id) return; // already hydrated, pristine, no need to re-set

    setTitle(item.title);
    setDescription(item.description ?? '');
    // Body is stored on `content.body` (jsonb). Fall back to '' if missing.
    const c: any = item.content;
    setBody(typeof c?.body === 'string' ? c.body : '');
    setItemType(item.itemType);
    setSourceReference(item.sourceReference ?? '');
    setIsSourceOfTruth(item.isSourceOfTruth);
    setCuratedByActorId(item.curatedByActorId ?? '');
    const evidence: any = c?.evidenceAttachmentIds;
    setEvidenceAttachmentIds(Array.isArray(evidence) ? evidence : []);
    hydratedForItemIdRef.current = item.id;
    setIsDirty(false);
  }, [item, isDirty]);

  /**
   * J7-W4 fix-wave 2 (B5) — one place to wrap a state setter so any user
   * mutation flips `isDirty=true`. Done as a thin closure factory so each
   * field's setter signature is unchanged at the callsite.
   */
  const markDirty = <T,>(setter: React.Dispatch<React.SetStateAction<T>>) => {
    return (next: React.SetStateAction<T>) => {
      setIsDirty(true);
      setter(next);
    };
  };

  if (itemQuery.isLoading) return <LoadingState />;
  if (itemQuery.isError || !item) {
    return (
      <RorosPage title={t('Knowledge Item')}>
        <EmptyState
          icon={<BookOpen className="h-6 w-6" aria-hidden />}
          title={t('Knowledge item not found')}
          description={t('This knowledge item may have been deleted or is in another organization.')}
          action={{
            label: t('Back to Knowledge'),
            onClick: () => navigate('/roros/artifacts/knowledge'),
          }}
        />
      </RorosPage>
    );
  }

  const freshness = computeFreshnessState(item.reviewDueAt);
  const canManageArtifacts = hasPermission(PERMISSIONS.ROROS_ARTIFACTS_EDIT);
  const canEditKnowledgeItem = canManageArtifacts || (!!currentUserId && item.createdBy === currentUserId);

  const handleSave = () => {
    if (!canEditKnowledgeItem) return;
    // AC-K-5 / L3: source_reference field hidden AND no longer sent in payload.
    // The source_reference DB column is retained but not read or written by the UI.
    const payload: UpdateKnowledgeItemPayload = {
      title,
      description,
      item_type: itemType,
      content: { body, evidenceAttachmentIds } as any,
      is_source_of_truth: isSourceOfTruth,
      curated_by_actor_id: curatedByActorId || null as any,
    };
    updateMutation.mutate(
      { domainId: writeDomainPath, id: item.id, payload },
      {
        // J7-W4 fix-wave 2 (B5): on a successful save the form values now
        // match the server, so clear dirty so a subsequent refetch can
        // re-hydrate normally.
        onSuccess: () => {
          setIsDirty(false);
          // Force a re-hydrate from the next refetched item.
          hydratedForItemIdRef.current = null;
          showToast.success(t('Knowledge item saved'));
        },
        onError: (err: any) => showToast.error(err?.message || t('Failed to save knowledge item')),
      },
    );
  };

  const handleMarkReviewed = () => {
    if (!canManageArtifacts) return;
    reviewMutation.mutate(
      { domainId: writeDomainPath, id: item.id },
      {
        onSuccess: () => showToast.success(t('Marked as reviewed')),
        onError: (err: any) => showToast.error(err?.message || t('Failed to mark as reviewed')),
      },
    );
  };

  const handleReprocess = () => {
    if (!canManageArtifacts) return;
    reprocessMutation.mutate(
      { domainId: writeDomainPath, id: item.id },
      {
        onSuccess: (data) => {
          if (data?.queued) {
            showToast.success(t('Reprocess queued — check back in a moment'));
          } else {
            showToast.info(t('No extractable attachments found'));
          }
        },
        onError: (err: any) => showToast.error(err?.message || t('Failed to queue reprocess')),
      },
    );
  };

  const handleAttachmentSelected = (attachmentId: string) => {
    if (!canEditKnowledgeItem) return;
    if (!evidenceAttachmentIds.includes(attachmentId)) {
      // J7-W4 fix-wave 2 (B5): adding an attachment is a user edit → dirty.
      markDirty(setEvidenceAttachmentIds)([...evidenceAttachmentIds, attachmentId]);
    }
    setPickerOpen(false);
  };

  const removeAttachment = (attachmentId: string) => {
    if (!canEditKnowledgeItem) return;
    markDirty(setEvidenceAttachmentIds)(evidenceAttachmentIds.filter((aid) => aid !== attachmentId));
  };

  return (
    <RorosPage
      title={t('Knowledge Item')}
      icon={<BookOpen className="h-5 w-5" aria-hidden />}
      actions={
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => navigate('/roros/artifacts/knowledge')}
            leftIcon={<ArrowLeft className="h-4 w-4" />}
          >
            {t('Back')}
          </Button>
          {canEditKnowledgeItem && (
            <Button
              onClick={handleSave}
              disabled={updateMutation.isPending}
              data-testid="knowledge-save-button"
            >
              {t('Save knowledge item')}
            </Button>
          )}
        </div>
      }
    >
      {/* ─── Header card with badges + freshness ──────────────────────── */}
      <Card className="mb-4" data-testid="knowledge-header-card">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-semibold">{item.title}</h2>
              <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                {t(item.itemType)}
              </span>
              {item.isSourceOfTruth && (
                <Badge variant="warning">
                  <Star className="h-3 w-3 mr-1" aria-hidden />
                  {t('Source of Truth')}
                </Badge>
              )}
              <FreshnessBadge reviewDueAt={item.reviewDueAt} />
            </div>
            {canManageArtifacts && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleMarkReviewed}
                disabled={reviewMutation.isPending}
                leftIcon={<CheckCircle2 className="h-4 w-4" />}
                data-testid="knowledge-mark-reviewed-button"
              >
                {t('Mark as reviewed')}
              </Button>
            )}
          </div>

          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-muted-foreground">
            <div>
              <span className="font-medium text-foreground">{t('Last reviewed')}:</span>{' '}
              {item.lastReviewedAt ? formatDate(item.lastReviewedAt) : t('Never reviewed')}
            </div>
            <div>
              <span className="font-medium text-foreground">{t('Next review due')}:</span>{' '}
              {item.reviewDueAt ? formatDate(item.reviewDueAt) : '—'}
            </div>
          </div>

          {freshness === 'overdue' && (
            <Alert variant="error" className="mt-3" data-testid="knowledge-overdue-banner">
              {t('Review overdue — please re-confirm this item is still accurate.')}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* ─── Body editor ─────────────────────────────────────────────── */}
      <Card className="mb-4">
        <CardContent className="p-4 space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground">{t('Title')}</label>
            <Input
              value={title}
              onChange={(e) => markDirty(setTitle)(e.target.value)}
              disabled={!canEditKnowledgeItem}
            />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground">{t('Description')}</label>
            <Textarea
              value={description}
              onChange={(e) => markDirty(setDescription)(e.target.value)}
              disabled={!canEditKnowledgeItem}
              rows={3}
            />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground">{t('Body')}</label>
            <Textarea
              value={body}
              onChange={(e) => markDirty(setBody)(e.target.value)}
              disabled={!canEditKnowledgeItem}
              rows={10}
              placeholder={t('Body content (markdown supported)')}
              data-testid="knowledge-body-editor"
            />
          </div>
        </CardContent>
      </Card>

      {/* ─── Metadata: SoT, item type, source ref, curator ───────────── */}
      <Card className="mb-4">
        <CardContent className="p-4 space-y-4">
          <Checkbox
            id="knowledge-sot-toggle"
            checked={isSourceOfTruth}
            onChange={(e) => markDirty(setIsSourceOfTruth)(e.target.checked)}
            disabled={!canEditKnowledgeItem}
            label={t('Mark as Source of Truth')}
            data-testid="knowledge-sot-toggle"
          />
          <div>
            <label className="text-sm font-medium text-foreground">{t('Item Type')}</label>
            <Select
              value={itemType}
              onChange={(e) => markDirty(setItemType)(e.target.value)}
              disabled={!canEditKnowledgeItem}
              options={ITEM_TYPE_OPTIONS}
            />
          </div>
          {/* AC-K-5: Source Reference URL field removed from form.
              The source_reference DB column is retained (unused) — no migration to drop it. */}
          <div>
            <label className="text-sm font-medium text-foreground">{t('Curator')}</label>
            <Select
              value={curatedByActorId || undefined}
              onChange={(e) => markDirty(setCuratedByActorId)(e.target.value)}
              disabled={!canEditKnowledgeItem}
              placeholder="—"
              options={actors.map((a) => ({ value: a.id, label: a.name }))}
            />
          </div>
        </CardContent>
      </Card>

      {/* ─── Evidence Attachments ────────────────────────────────────── */}
      <Card className="mb-4" data-testid="knowledge-evidence-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold">{t('Evidence Attachments')}</h3>
            {canEditKnowledgeItem && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPickerOpen(true)}
                data-testid="knowledge-add-evidence-button"
              >
                {t('Add evidence attachment')}
              </Button>
            )}
          </div>
          {evidenceAttachmentIds.length === 0 ? (
            <p className="text-xs text-muted-foreground">{t('None')}</p>
          ) : (
            <ul className="space-y-1">
              {evidenceAttachmentIds.map((aid) => (
                <li key={aid} className="flex items-center justify-between text-xs">
                  <span className="font-mono truncate max-w-[280px]">{aid}</span>
                  {canEditKnowledgeItem && (
                    <Button variant="ghost" size="sm" onClick={() => removeAttachment(aid)}>
                      {t('Remove')}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* ─── Extracted content panel (AC-I5b) + S-1 Reprocess ──────── */}
      <ExtractionStatusPanel
        content={item?.content as Record<string, any> | null | undefined}
        evidenceAttachmentIds={evidenceAttachmentIds}
        onReprocess={canManageArtifacts ? handleReprocess : undefined}
        isReprocessing={canManageArtifacts ? reprocessMutation.isPending : false}
      />

      {/* ─── Linked Work Items (ART-03) ──────────────────────────────── */}
      <Card className="mb-4" data-testid="knowledge-linked-work-items-card">
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold mb-3">{t('Linked Work Items')}</h3>
          {linkedWorkItems.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              {t('No work items currently link to this knowledge item.')}
            </p>
          ) : (
            <ul className="space-y-1.5" role="list">
              {linkedWorkItems.slice(0, 10).map((wi) => (
                <li key={wi.id} className="flex items-center justify-between text-xs">
                  <span className="truncate max-w-[400px]">{wi.title}</span>
                  <Badge variant="secondary">{wi.status}</Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Attachments picker modal */}
      {pickerOpen && canEditKnowledgeItem && (
        <AttachmentsManager
          onClose={() => setPickerOpen(false)}
          pickMode={true}
          onSelect={handleAttachmentSelected}
        />
      )}
    </RorosPage>
  );
}
